// src/components/AuditManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import Papa from 'papaparse';

// Import the other components to be used as tabs
import AnnualAuditSupport from './AnnualAuditSupport';
import AnnualCertificateManagement from './AnnualCertificateManagement';
import ConflictCheckModal from './ConflictCheckModal';
import { canAccessTab } from '../utils/rbac';
import { fetchAllPages } from '../api';

// [Tier 1 fix] Single source of truth for the page size this component
// requests — previously the page-count math (`Math.ceil(pagination.count /
// 10)`, in two places) hardcoded the backend's default page_size as a bare
// magic number instead of deriving it from what was actually requested, so
// the two could silently drift apart.
const AUDIT_LOG_PAGE_SIZE = 10;

const AuditManagement = ({ authAxios, users, clients, showFormMessage, userRole, navigateToItem, navigateTo, setNavigateTo, navigateToTrustLedger }) => {
    // --- RBAC Permissions ---
    // [WF-B3] Was `userRole === 'PARTNER' || userRole === 'SUPERUSER'` — the
    // backend's auditor_allowed_views whitelist and TAB_ACCESS.auditManagement
    // both already grant AUDITOR this whole page, but this local gate hid it
    // regardless. Delegating to canAccessTab()/TAB_ACCESS directly (rather
    // than adding 'AUDITOR' to a redeclared local array) is the structural
    // fix — this exact "sidebar/backend say yes, component says no" pattern
    // has already recurred independently at least 3 times in this codebase.
    const isPartner = canAccessTab(userRole, 'auditManagement');

    const [auditLogs, setAuditLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [filters, setFilters] = useState({
        user: '',
        action: '',
        start_date: '',
        end_date: '',
        search: '',
    });
    const [pagination, setPagination] = useState({
        count: 0,
        next: null,
        previous: null,
        page: 1,
    });
    
    const [activeSubTab, setActiveSubTab] = useState('auditTrail');
        // Allow deep-linking into audit subtabs (e.g., from Trust Accounting module).
        const [annualAuditPrefill, setAnnualAuditPrefill] = useState(null);

        useEffect(() => {
            if (!navigateTo || navigateTo.tab !== 'auditManagement') return;

            if (navigateTo.type === 'audit_subtab') {
                if (navigateTo.subTab) setActiveSubTab(navigateTo.subTab);

                setAnnualAuditPrefill({
                    startDate: navigateTo.startDate,
                    endDate: navigateTo.endDate,
                    activeReport: navigateTo.reportKey,
                });

                if (typeof setNavigateTo === 'function') setNavigateTo(null);
            }
        }, [navigateTo, setNavigateTo]);

    const [showConflictModal, setShowConflictModal] = useState(false);
    const [selectedConflictCheck, setSelectedConflictCheck] = useState(null);

    const actionOptions = [
        { value: 'CREATE', label: 'Create', color: 'bg-green-100 text-green-800' },
        { value: 'UPDATE', label: 'Update', color: 'bg-blue-100 text-blue-800' },
        { value: 'DELETE', label: 'Delete', color: 'bg-red-100 text-red-800' },
        { value: 'VIEW', label: 'View', color: 'bg-gray-100 text-gray-800' },
        { value: 'LOGIN', label: 'Login', color: 'bg-purple-100 text-purple-800' },
        { value: 'LOGOUT', label: 'Logout', color: 'bg-gray-100 text-gray-600' },
        { value: 'EXPORT', label: 'Export', color: 'bg-blue-100 text-blue-800' },
        { value: 'DOWNLOAD', label: 'Download', color: 'bg-blue-100 text-blue-800' },
        { value: 'IMPORT', label: 'Import', color: 'bg-blue-100 text-blue-800' },
        { value: 'CASH_TRANSACTION_FLAGGED', label: 'Flagged Transactions', color: 'bg-amber-100 text-amber-800' },
    ];

    const fetchAuditLogs = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, page_size: String(AUDIT_LOG_PAGE_SIZE) });
            if (filters.user) params.append('user', filters.user);
            if (filters.action) params.append('action', filters.action);
            if (filters.start_date) params.append('timestamp__date__gte', filters.start_date);
            if (filters.end_date) params.append('timestamp__date__lte', filters.end_date);
            if (filters.search) params.append('search', filters.search);


            const response = await authAxios.get('audit-logs/', { params });
            setAuditLogs(response.data.results);
            setPagination({
                count: response.data.count,
                next: response.data.next,
                previous: response.data.previous,
                page: page,
            });
        } catch (err) {
            showFormMessage('danger', 'Failed to fetch audit logs.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [authAxios, filters, showFormMessage]);

    useEffect(() => {
        if (isPartner && activeSubTab === 'auditTrail') {
            fetchAuditLogs(1);
        }
    }, [fetchAuditLogs, activeSubTab, isPartner]);
    
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchAuditLogs(1);
    };
    
    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= Math.ceil(pagination.count / AUDIT_LOG_PAGE_SIZE)) {
            fetchAuditLogs(newPage);
        }
    };

    // --- Handle CSV Export ---
    const handleExport = async () => {
        setExporting(true);
        try {
            // [Tier 1 fix] This used to request page_size=1000 in one shot —
            // silently clamped to CustomPagination's max_page_size=100 by
            // DRF, so "export all" only ever exported the first 100 matching
            // rows with no indication the rest were missing. fetchAllPages
            // walks every page instead, so the export is genuinely complete.
            const exportParams = {};
            if (filters.user) exportParams.user = filters.user;
            if (filters.action) exportParams.action = filters.action;
            if (filters.start_date) exportParams.timestamp__date__gte = filters.start_date;
            if (filters.end_date) exportParams.timestamp__date__lte = filters.end_date;
            if (filters.search) exportParams.search = filters.search;

            const results = await fetchAllPages(authAxios, 'audit-logs/', exportParams);

            const csvData = results.map(log => ({
                Timestamp: format(new Date(log.timestamp), 'yyyy-MM-dd HH:mm:ss'),
                User: log.user ? log.user.username : 'System',
                Action: log.action,
                Object: log.content_object_repr || '-',
                Description: log.description || '',
                Details: JSON.stringify(log.details || {}),
                IP_Address: log.ip_address || ''
            }));

            const csv = Papa.unparse(csvData);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `audit_trail_${format(new Date(), 'yyyyMMdd')}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            showFormMessage('success', 'Audit log exported successfully.');
        } catch (err) {
            console.error(err);
            showFormMessage('danger', 'Failed to export logs.');
        } finally {
            setExporting(false);
        }
    };

    // --- Smart Details Viewer with "Diff" logic ---
    const DetailsViewer = ({ data }) => {
        if (!data || typeof data !== 'object') return <span className="text-gray-400 italic">No details</span>;

        // Check if this is a diff-style change log (old/new or from/to pattern)
        const isDiff = Object.values(data).some(val => 
            val && typeof val === 'object' && ('old' in val || 'new' in val || 'from' in val || 'to' in val)
        );

        if (isDiff) {
            return (
                <div className="space-y-1 text-xs">
                    {Object.entries(data).map(([field, change]) => {
                        if (typeof change !== 'object') return null;
                        const from = change.old || change.from || '—';
                        const to = change.new || change.to || '—';
                        
                        return (
                            <div key={field} className="grid grid-cols-[auto_1fr] gap-2">
                                <span className="font-semibold text-gray-700">{field}:</span>
                                <span>
                                    <span className="bg-red-50 text-red-700 px-1 rounded line-through mr-1">{String(from)}</span>
                                    <i className="bi bi-arrow-right text-gray-400 mx-1"></i>
                                    <span className="bg-green-50 text-green-700 px-1 rounded">{String(to)}</span>
                                </span>
                            </div>
                        );
                    })}
                </div>
            );
        }

        // Standard Key-Value view
        return (
            <div className="space-y-1">
                {Object.entries(data).map(([key, value]) => {
                    if (key === 'action_by') return null;
                    return (
                        <div key={key} className="text-xs break-all">
                            <span className="font-semibold text-gray-600 mr-1">{key.replace(/_/g, ' ')}:</span>
                            <span className="text-gray-800">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                        </div>
                    );
                })}
            </div>
        );
    };

    // --- Navigate to Object ---
    const handleObjectClick = async (log) => {
        if (!log || !log.content_object_repr) return;

        const repr = (log.content_object_repr || '').toLowerCase();

        // If this audit entry refers to a ConflictCheck object, fetch and open in read-only modal
        if (repr.includes('conflict')) {
            try {
                const resp = await authAxios.get(`conflict-checks/${log.object_id}/`);
                setSelectedConflictCheck(resp.data);
                setShowConflictModal(true);
                return;
            } catch (err) {
                console.error('Failed to load conflict check details:', err);
                showFormMessage('danger', 'Failed to load conflict details.');
                return;
            }
        }

        if (!navigateToItem || !log.object_id) return;

        // Infer type from content_object_repr string
        let type = null;
        if (repr.includes('case')) type = 'case_open';
        else if (repr.includes('invoice')) type = 'invoice_due';
        else if (repr.includes('client')) type = 'client';
        else if (repr.includes('task')) type = 'task_due';
        else if (repr.includes('document')) type = 'document';
        else if (repr.includes('time entry')) type = 'time_entry';
        else if (repr.includes('expense')) type = 'expense_entry';
        else if (repr.includes('communication')) type = 'communication_log';

        if (type) {
            navigateToItem({ type, id: log.object_id });
        }
    };


    if (!isPartner) {
        return (
            <div className="p-4">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <strong className="font-bold">Access Denied: </strong>
                    <span className="block sm:inline">You do not have the required permissions to access the Audit Management section. This area is restricted to Partners.</span>
                </div>
            </div>
        );
    }

    const tabs = [
        { id: 'auditTrail', label: 'Audit Trail' },
        { id: 'annualSupport', label: 'Annual Audit Support' },
        { id: 'certificateMgmt', label: 'Annual Certificate Management' },
    ];

    return (
        <div className="p-4 sm:p-6 animate__animated animate__fadeIn">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">Audit Management</h1>
            
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveSubTab(tab.id)}
                            className={`${
                                activeSubTab === tab.id
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            <div className="mt-6">
                {activeSubTab === 'auditTrail' && (
                    <div className="space-y-6">
                        {/* Filters Panel */}
                        <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                            <div className="flex justify-between items-center mb-4">
                                <h5 className="text-lg font-semibold text-gray-800">Search & Filter Logs</h5>
                                <button 
                                    onClick={handleExport} 
                                    disabled={exporting}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                >
                                    {exporting ? (
                                        <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-700 mr-2"></div> Exporting...</>
                                    ) : (
                                        <><i className="bi bi-download mr-2"></i> Export CSV</>
                                    )}
                                </button>
                            </div>
                            
                            <form onSubmit={handleFilterSubmit}>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
                                    {/* Text Search */}
                                    <div className="lg:col-span-2">
                                        <label htmlFor="search-filter" className="block text-xs font-medium text-gray-700 uppercase tracking-wide">Search</label>
                                        <input 
                                            type="text" 
                                            id="search-filter" 
                                            name="search" 
                                            placeholder="Description, details, or IP..."
                                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            value={filters.search} 
                                            onChange={handleFilterChange} 
                                        />
                                    </div>
                                    <div className="lg:col-span-1">
                                        <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide">User</label>
                                        <select name="user" className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={filters.user} onChange={handleFilterChange}>
                                            <option value="">All Users</option>
                                            {(users || []).map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
                                        </select>
                                    </div>
                                    <div className="lg:col-span-1">
                                        <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide">Action</label>
                                        <select name="action" className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={filters.action} onChange={handleFilterChange}>
                                            <option value="">All Actions</option>
                                            {actionOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="lg:col-span-1">
                                        <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide">Date Range</label>
                                        <div className="flex gap-1">
                                            <input type="date" name="start_date" className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-xs" value={filters.start_date} onChange={handleFilterChange} placeholder="From" />
                                        </div>
                                    </div>
                                    <div className="lg:col-span-1">
                                        <button type="submit" className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
                                            Filter Logs
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Audit Log Table */}
                        <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                            {loading ? (
                                <div className="p-12 text-center text-gray-500">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                                    Loading records...
                                </div>
                            ) : (
                                <>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target Object</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Changes / Details</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {auditLogs.length > 0 ? auditLogs.map(log => {
                                                    const actionStyle = actionOptions.find(o => o.value === log.action)?.color || 'bg-gray-100 text-gray-800';
                                                    return (
                                                        <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {format(new Date(log.timestamp), 'MMM dd, yyyy HH:mm')}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                <div className="flex items-center">
                                                                    <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center text-xs text-blue-600 font-bold mr-2">
                                                                        {(log.user?.username || 'S')[0].toUpperCase()}
                                                                    </div>
                                                                    {log.user ? log.user.username : 'System'}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${actionStyle}`}>
                                                                    {log.action}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                                <div 
                                                                    className={`font-medium ${navigateToItem && log.object_id ? 'text-blue-600 hover:underline cursor-pointer' : ''}`}
                                                                    onClick={() => handleObjectClick(log)}
                                                                    title={navigateToItem && log.object_id ? "Click to view item" : ""}
                                                                >
                                                                    {log.content_object_repr || '-'}
                                                                </div>
                                                                <div className="text-xs text-gray-400 mt-0.5">{log.description}</div>
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                                <DetailsViewer data={log.details} />
                                                                {log.ip_address && <div className="text-xs text-gray-300 mt-1">IP: {log.ip_address}</div>}
                                                            </td>
                                                        </tr>
                                                    );
                                                }) : (
                                                    <tr>
                                                        <td colSpan="5" className="px-6 py-10 text-center text-sm text-gray-500">
                                                            No audit records match your filters.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    {/* Pagination Controls */}
                                    {pagination.count > 0 && (
                                        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-700">
                                                        Showing page <span className="font-medium">{pagination.page}</span> of <span className="font-medium">{Math.ceil(pagination.count / AUDIT_LOG_PAGE_SIZE)}</span>
                                                    </p>
                                                </div>
                                                <div>
                                                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                                        <button 
                                                            onClick={() => fetchAuditLogs(pagination.page - 1)} 
                                                            disabled={!pagination.previous}
                                                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                                        >
                                                            Previous
                                                        </button>
                                                        <button 
                                                            onClick={() => fetchAuditLogs(pagination.page + 1)} 
                                                            disabled={!pagination.next}
                                                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                                        >
                                                            Next
                                                        </button>
                                                    </nav>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
                {activeSubTab === 'annualSupport' && (
                    <AnnualAuditSupport
                        authAxios={authAxios}
                        clients={clients || []}
                        showFormMessage={showFormMessage}
                        userRole={userRole}
                        navigateToTrustLedger={navigateToTrustLedger}
                        initialStartDate={annualAuditPrefill?.startDate}
                        initialEndDate={annualAuditPrefill?.endDate}
                        initialActiveReport={annualAuditPrefill?.activeReport}
                    />
                )}
                {activeSubTab === 'certificateMgmt' && (
                    <AnnualCertificateManagement authAxios={authAxios} users={users || []} showFormMessage={showFormMessage} userRole={userRole} />
                )}
            </div>
            {/* Conflict Check modal (read-only) */}
            {selectedConflictCheck && (
                <ConflictCheckModal
                    show={showConflictModal}
                    onClose={() => { setShowConflictModal(false); setSelectedConflictCheck(null); }}
                    conflictCheckResult={selectedConflictCheck}
                    authAxios={authAxios}
                    onConflictResolved={(updated) => { setSelectedConflictCheck(updated); fetchAuditLogs(pagination.page); setShowConflictModal(false); }}
                    readOnly={true}
                />
            )}
        </div>
    );
};

export default AuditManagement;
